import {
  ArrowBackIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  RepeatClockIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
  Flex,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { FiFileText } from "react-icons/fi";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/Auth";
import { usePremium } from "../../../contexts/Premium";
import { isReadingTrackingReadOnly } from "../../reading/readingAccess";
import { getComic, getReading } from "../../../services/readingOrdersApi";
import PremiumBanner from "../../../shared/components/PremiumBanner";
import SectionTitle from "../../../shared/components/SectionTitle";
import Spinner from "../../../shared/components/Spinner";
import { trackEvent } from "../../../shared/analytics";
import useChangeStatus from "../../../shared/useChangeStatus";
import ComicActionButton from "../components/ComicActionButton";
import ComicNotes from "../components/ComicNotes";
import ComicOptionsMenu from "../components/ComicOptionsMenu";
import ComicQuickAction from "../components/ComicQuickAction";
import NewLinkForm from "../components/NewLinkForm";

const HERO_BG = "#151A30";
const PAGE_BG = "#101426";

// Mirrors the mobile ComicScreen. The mobile hero collapses as the details
// scroll under it; on web the hero simply sits above the details instead.
const ComicDetail = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { comicId } = useParams();
  const { authData } = useAuth();
  const { isPremium } = usePremium();
  const toast = useToast({ duration: 5000, isClosable: true });
  const notesSectionRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const listComic = state?.comic;
  const readingId = state?.readingId || listComic?.readingId || null;
  const previewOnly = !!(state?.previewOnly || listComic?.previewOnly);
  const isAdmin = authData?.role === 1;

  const readingQuery = useQuery(
    ["reading", readingId],
    () => getReading(readingId),
    { enabled: !!readingId }
  );
  const readingData = readingQuery?.data?.data;
  const readOnly = isReadingTrackingReadOnly(readingData, isPremium);
  // Tracking and direct links stay locked until we know the reading is
  // writable, exactly as the mobile screen gates its action bar.
  const actionsAvailable = !readOnly && (!readingId || !!readingData);
  const externalLinksAvailable = !previewOnly && actionsAvailable;
  const actionBarDisabled = !externalLinksAvailable;

  const comicQuery = useQuery(["comic", comicId, listComic?.story], () =>
    getComic(comicId, listComic?.story)
  );

  const {
    markAsReadActionOnly,
    markAsSkippedActionOnly,
    markAsReadLaterActionOnly,
    markAsUnreadActionOnly,
  } = useChangeStatus(readingId);

  useEffect(() => {
    if (selectedIndex > 1 && !isAdmin) {
      setSelectedIndex(0);
    }
  }, [isAdmin, selectedIndex]);

  if (comicQuery.isLoading) {
    return <Spinner />;
  }

  // The row we navigated from holds the freshest reading status, so it wins
  // over the fetched issue, same as the mobile screen.
  const comic = { ...comicQuery.data?.data, ...(listComic || {}) };
  const notes = comicQuery.data?.data?.notes ?? listComic?.notes ?? [];
  const hasOwnNote = Array.isArray(notes)
    ? notes.some((note) => note.isMine || note.is_mine)
    : false;
  const showTrackingActions = !previewOnly && !!readingId;
  const showNoteAction = showTrackingActions && !hasOwnNote;
  const openedFromProfile = state?.source === "profile";
  const totalComics = readingData?.totalComics || comic.totalComics || null;
  const showActionBar =
    previewOnly || !!readingId || !!comic.url || !!comic.lookupUrl;
  const hasDirectReadLink =
    !!comic.url && (comic.urlType === "marvel" || comic.urlType === "dcui");
  const unresolved = !comic.skipped && !comic.read;

  // Copying the title to the clipboard is a Premium-only convenience.
  const canCopyTitle = !!isPremium;

  const onCopy = async () => {
    if (!canCopyTitle) return;
    try {
      await navigator.clipboard.writeText(comic.title);
    } catch (error) {
      return;
    }
    toast({
      title: "Title copied",
      description: "The title of the issue was added to your clipboard",
      status: "info",
    });
  };

  const onPressLookup = () => {
    trackEvent("comic_details_lookup_button_click");
    window.open(comic.lookupUrl, "_blank")?.focus();
  };

  const onPressRead = comic.url
    ? () => {
        trackEvent("comic_details_read_button_click");
        window.open(comic.url, "_blank")?.focus();
      }
    : onPressLookup;

  const onStatusChanged = () => {
    toast({
      title: "Progress updated",
      description: "That issue status been updated.",
      status: "success",
    });
    navigate(-1);
  };

  const onPressMarkAsRead = async (skipped) => {
    trackEvent(
      skipped
        ? "comic_details_skip_button_click"
        : "comic_details_mark_as_read_button_click"
    );
    const updated = await (skipped
      ? markAsSkippedActionOnly(comic.id, comic.story)
      : markAsReadActionOnly(comic.id, comic.story));
    if (!updated) return;
    onStatusChanged();
  };

  const onPressMarkAsUnread = async () => {
    trackEvent(
      comic.skipped
        ? "comic_details_skip_button_click"
        : "comic_details_mark_as_read_button_click"
    );
    const updated = await markAsUnreadActionOnly(
      comic.id,
      comic.story,
      comic.skipped,
      comic.readLater
    );
    if (!updated) return;
    onStatusChanged();
  };

  const onPressReadLater = async () => {
    trackEvent("comic_details_read_later_button_click");
    const updated = await markAsReadLaterActionOnly(comic.id, comic.story);
    if (!updated) return;
    navigate(-1);
  };

  const onPressNote = () => {
    trackEvent("comic_details_note_button_click");
    if (!authData) {
      navigate("/login");
      return;
    }

    setSelectedIndex(1);
    window.setTimeout(() => notesSectionRef.current?.openComposer(), 50);
  };

  const details = (
    <Box px="24px" pt="28px" pb="32px">
      <SectionTitle>ABOUT</SectionTitle>
      <Text
        pt="14px"
        color={comic.description ? "#F7F9FC" : "#8F9BB3"}
        fontSize="17px"
        lineHeight="25px"
        whiteSpace="pre-line"
      >
        {comic.description || "No description available."}
      </Text>
      {!unresolved && (
        <Flex
          mt="24px"
          p="16px"
          alignItems="center"
          borderWidth="1px"
          borderColor="#30384E"
          borderRadius="14px"
          bg="#1A2138"
        >
          <Box pr="16px" flexShrink={0}>
            {comic.skipped ? (
              <ArrowRightIcon color="#E67E22" boxSize="24px" />
            ) : (
              <CheckCircleIcon color="#27AE60" boxSize="28px" />
            )}
          </Box>
          <Box flex="1" minW={0}>
            <Text fontSize="14px" lineHeight="20px">
              You have{" "}
              <Text
                as="span"
                color={comic.skipped ? "#E67E22" : "#27AE60"}
                fontWeight="700"
              >
                {comic.skipped ? "skipped" : "read"}
              </Text>{" "}
              this comic on{" "}
              <Text as="span" fontWeight="700">
                {comic.readingDateFormatted}
              </Text>
            </Text>
          </Box>
        </Flex>
      )}
    </Box>
  );

  const hero = (
    <Box bg={HERO_BG} borderTopRadius="lg">
      <Flex
        direction="column"
        alignItems="center"
        px="16px"
        pt="24px"
        pb="18px"
      >
        <Box
          p="4px"
          borderRadius="12px"
          bg="#222B45"
          boxShadow="0 6px 12px rgba(0, 0, 0, 0.32)"
        >
          <Box
            as="img"
            src={comic.thumbnail}
            alt={`${comic.title} cover`}
            w="150px"
            h="230px"
            objectFit="cover"
            borderRadius="8px"
          />
        </Box>

        <Flex
          direction="column"
          alignItems="center"
          alignSelf="stretch"
          minW={0}
          mt="16px"
        >
          {!!readingId && !!comic.order && !!totalComics && (
            <Text
              color="#8F9BB3"
              textAlign="center"
              fontSize="12px"
              lineHeight="16px"
              fontWeight="700"
              letterSpacing="0.8px"
            >
              {comic.order} OF {totalComics}
            </Text>
          )}
          <Text
            as="h2"
            color="#F7F9FC"
            textAlign="center"
            fontSize="22px"
            lineHeight="28px"
            fontWeight="700"
            letterSpacing="-0.35px"
            cursor={canCopyTitle ? "pointer" : "default"}
            title={canCopyTitle ? "Copy title" : undefined}
            onClick={canCopyTitle ? onCopy : undefined}
          >
            {comic.title}
          </Text>
          {!!comic.story && (
            <Box
              mt="10px"
              px="10px"
              py="4px"
              borderRadius="full"
              borderWidth="1px"
              borderColor="#3C4968"
              bg="#1A2138"
            >
              <Text
                color="#A8B2C8"
                fontSize="10px"
                lineHeight="14px"
                fontWeight="700"
                letterSpacing="0.8px"
              >
                {comic.story} STORY
              </Text>
            </Box>
          )}
        </Flex>

        {showActionBar && hasDirectReadLink && (
          <ComicActionButton
            isDisabled={actionBarDisabled}
            urlType={comic.urlType}
            onClick={onPressRead}
            mt="24px"
            mb="18px"
            borderRadius="12px"
          />
        )}

        {showActionBar && (
          <HStack
            alignSelf="stretch"
            justifyContent="space-around"
            alignItems="flex-start"
            px="4px"
            spacing="0"
          >
            <ComicQuickAction
              icon={<SearchIcon boxSize="18px" />}
              label="Lookup"
              onClick={onPressLookup}
            />
            {!!readingId && unresolved && (
              <ComicQuickAction
                isDisabled={actionBarDisabled}
                icon={<ArrowRightIcon boxSize="16px" />}
                label="Skip"
                color="#E67E22"
                onClick={() => onPressMarkAsRead(1)}
              />
            )}
            {!!readingId && !comic.readLater && (
              <ComicQuickAction
                isDisabled={actionBarDisabled}
                icon={<RepeatClockIcon boxSize="18px" />}
                label="Read later"
                color="#A8B2C8"
                onClick={onPressReadLater}
              />
            )}
            {!!readingId && (
              <ComicQuickAction
                isDisabled={actionBarDisabled}
                icon={<CheckCircleIcon boxSize="18px" />}
                label={unresolved ? "Mark as read" : "Mark as unread"}
                color="#27AE60"
                onClick={
                  unresolved ? () => onPressMarkAsRead(0) : onPressMarkAsUnread
                }
              />
            )}
            {showNoteAction && (
              <ComicQuickAction
                icon={<FiFileText size="18px" />}
                label="Note"
                onClick={onPressNote}
              />
            )}
          </HStack>
        )}

        {!!readingId && !isPremium && (previewOnly || readOnly) && (
          <PremiumBanner
            mt="18px"
            title="Go Premium to unlock tracking and direct links."
          />
        )}
      </Flex>
    </Box>
  );

  return (
    <Container maxW="container.md" px="2" pb="12">
      <Flex as="header" alignItems="center" pt="4">
        <Box flex="1" minW={0}>
          <Breadcrumb p="2" fontSize="sm">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={-1}>
                <ArrowBackIcon /> Go Back
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink noOfLines={1}>
                {comic.title} {comic.story && `[${comic.story}]`}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </Box>
        <ComicOptionsMenu comic={comic} />
      </Flex>

      <Box bg={PAGE_BG} borderRadius="lg" overflow="hidden">
        <Tabs
          index={selectedIndex}
          onChange={setSelectedIndex}
          variant="line"
          colorScheme="red"
          isLazy
        >
          {hero}
          <Box bg={HERO_BG} borderTopWidth="1px" borderTopColor="#252E49">
            <TabList px="16px" borderBottom="none">
              <Tab fontSize="12px" fontWeight="700" letterSpacing="0.8px">
                DETAILS
              </Tab>
              <Tab fontSize="12px" fontWeight="700" letterSpacing="0.8px">
                NOTES
              </Tab>
              {isAdmin && (
                <Tab fontSize="12px" fontWeight="700" letterSpacing="0.8px">
                  ADMIN
                </Tab>
              )}
            </TabList>
          </Box>
          <TabPanels>
            <TabPanel p="0">{details}</TabPanel>
            <TabPanel p="0">
              <ComicNotes
                ref={notesSectionRef}
                authData={authData}
                comicId={comic.id}
                notes={notes}
                onRequireAuth={() => navigate("/login")}
                showOwnNoteSection={
                  showTrackingActions || (openedFromProfile && hasOwnNote)
                }
              />
            </TabPanel>
            {isAdmin && (
              <TabPanel p="0">
                <Box px="24px" pt="28px" pb="32px">
                  <SectionTitle>ADMIN</SectionTitle>
                  <Box pt="16px">
                    <NewLinkForm comicId={comic.id} />
                  </Box>
                </Box>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Box>
    </Container>
  );
};

export default ComicDetail;
